import { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICE_OPTIONS, PROPERTY_TYPES } from "@/lib/constants";
import { Loader2, ShieldCheck, CalendarClock } from "lucide-react";

const LeadDialogContext = createContext(null);
export const useLeadDialog = () => useContext(LeadDialogContext);

const TIME_WINDOWS = ["Morning (8am - 12pm)", "Afternoon (12pm - 4pm)", "Evening (4pm - 7pm)"];
const URGENCY = ["Just exploring", "Within 1-3 months", "As soon as possible", "Emergency"];

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  property_type: "",
  urgency: "",
  site_address: "",
  preferred_date: "",
  preferred_time: "",
  message: "",
  services: [],
};

export function LeadDialogProvider({ children }) {
  const [mode, setMode] = useState(null); // 'quote' | 'survey' | null
  const openQuote = () => setMode("quote");
  const openSurvey = () => setMode("survey");

  return (
    <LeadDialogContext.Provider value={{ openQuote, openSurvey }}>
      {children}
      <LeadDialog mode={mode} onClose={() => setMode(null)} />
    </LeadDialogContext.Provider>
  );
}

function LeadDialog({ mode, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const isSurvey = mode === "survey";
  const open = mode !== null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (svc) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(svc) ? f.services.filter((s) => s !== svc) : [...f.services, svc],
    }));

  const handleOpenChange = (o) => {
    if (!o) {
      onClose();
      setTimeout(() => setForm(EMPTY), 200);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(isSurvey ? "/survey" : "/quote", form);
      toast.success(
        isSurvey ? "Survey request received! We'll confirm your time shortly." : "Quote request sent! Our team will be in touch soon."
      );
      handleOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={isSurvey ? "survey-dialog" : "quote-dialog"}
        className="max-w-lg bg-[#0A142F] border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0066FF]/15 text-[#38BDF8] ring-1 ring-[#38BDF8]/30">
              {isSurvey ? <CalendarClock className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
            </span>
            <div>
              <DialogTitle className="font-display text-xl text-white">
                {isSurvey ? "Book a Free Site Survey" : "Request a Quote"}
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                {isSurvey
                  ? "Pick a preferred time — we'll confirm and send a technician."
                  : "Tell us what you need and we'll build a tailored proposal."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name *">
              <Input data-testid="lead-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" className="bg-[#050B18] border-slate-700" />
            </Field>
            <Field label="Company">
              <Input data-testid="lead-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company (optional)" className="bg-[#050B18] border-slate-700" />
            </Field>
            <Field label="Email *">
              <Input data-testid="lead-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="bg-[#050B18] border-slate-700" />
            </Field>
            <Field label="Phone">
              <Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(931) 555-0199" className="bg-[#050B18] border-slate-700" />
            </Field>
          </div>

          <Field label="Property Type">
            <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger data-testid="lead-property-type" className="bg-[#050B18] border-slate-700">
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A142F] border-slate-700 text-slate-100">
                {PROPERTY_TYPES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={isSurvey ? "Systems to Survey" : "Services of Interest"}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SERVICE_OPTIONS.map((svc) => (
                <label
                  key={svc}
                  className="flex items-center gap-2 rounded-lg border border-slate-700 bg-[#050B18] px-3 py-2 cursor-pointer hover:border-[#38BDF8]/50 transition-colors"
                >
                  <Checkbox
                    data-testid={`lead-service-${svc.split(" ")[0].toLowerCase()}`}
                    checked={form.services.includes(svc)}
                    onCheckedChange={() => toggleService(svc)}
                  />
                  <span className="text-sm text-slate-300">{svc.split(" ")[0]}</span>
                </label>
              ))}
            </div>
          </Field>

          {isSurvey && (
            <>
              <Field label="Site Address / Area">
                <Input data-testid="lead-address-input" value={form.site_address} onChange={(e) => set("site_address", e.target.value)} placeholder="123 Main St, Nashville, TN" className="bg-[#050B18] border-slate-700" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Preferred Date">
                  <Input data-testid="lead-date-input" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className="bg-[#050B18] border-slate-700" />
                </Field>
                <Field label="Preferred Time">
                  <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                    <SelectTrigger data-testid="lead-time-input" className="bg-[#050B18] border-slate-700">
                      <SelectValue placeholder="Select a window" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A142F] border-slate-700 text-slate-100">
                      {TIME_WINDOWS.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </>
          )}

          {!isSurvey && (
            <Field label="Urgency">
              <Select value={form.urgency} onValueChange={(v) => set("urgency", v)}>
                <SelectTrigger data-testid="lead-urgency-input" className="bg-[#050B18] border-slate-700">
                  <SelectValue placeholder="How soon do you need this?" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A142F] border-slate-700 text-slate-100">
                  {URGENCY.map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field label={isSurvey ? "Anything we should know?" : "Project Details"}>
            <Textarea
              data-testid="lead-message-input"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Tell us a bit about your property and goals..."
              rows={3}
              className="bg-[#050B18] border-slate-700"
            />
          </Field>

          <Button
            data-testid={isSurvey ? "survey-form-submit-btn" : "quote-form-submit-btn"}
            type="submit"
            disabled={submitting}
            className="w-full bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold h-11 glow-blue"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isSurvey ? "Request My Survey" : "Send My Quote Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
